import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  Autocomplete,
  Card,
  CardContent,
  Stack,
  Chip,
  Typography,
  Box,
} from '@mui/material';

const TeamForm = ({ open, mode, formData, formErrors, employees, selectedTeam, onClose, onSubmit, onInputChange }) => {
  const getDialogTitle = () => {
    switch (mode) {
      case 'add':
        return 'Add New Team';
      case 'edit':
        return 'Edit Team';
      case 'view':
        return 'View Team';
      default:
        return 'Team';
    }
  };

  const isViewMode = mode === 'view';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={onSubmit} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Team Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Team Name"
                value={formData.name}
                onChange={(e) => onInputChange('name', e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
                disabled={isViewMode}
                required
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => onInputChange('description', e.target.value)}
                error={!!formErrors.description}
                helperText={formErrors.description}
                disabled={isViewMode}
              />
            </Grid>

            {/* Team Lead */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => `${option.full_name} - ${option.designation}`}
                value={employees.find((emp) => emp.id === formData.team_lead_id) || null}
                onChange={(event, newValue) => {
                  onInputChange('team_lead_id', newValue ? newValue.id : '');
                }}
                disabled={isViewMode}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Team Lead"
                    placeholder="Select team lead (optional)"
                    error={!!formErrors.team_lead_id}
                    helperText={formErrors.team_lead_id}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        const nextField = document.querySelector('[name="supervisor"]');
                        if (nextField) nextField.focus();
                      }
                    }}
                  />
                )}
              />
            </Grid>

            {/* Supervisor */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={employees}
                getOptionLabel={(option) => `${option.full_name} - ${option.designation}`}
                value={employees.find((emp) => emp.id === formData.supervisor_id) || null}
                onChange={(event, newValue) => {
                  onInputChange('supervisor_id', newValue ? newValue.id : '');
                }}
                disabled={isViewMode}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="supervisor"
                    label="Supervisor"
                    placeholder="Select supervisor (optional)"
                    error={!!formErrors.supervisor_id}
                    helperText={formErrors.supervisor_id}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        const nextField = document.querySelector('[data-testid="team-members-autocomplete"]');
                        if (nextField) nextField.focus();
                      }
                    }}
                  />
                )}
              />
            </Grid>

            {/* Team Members */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={employees}
                getOptionLabel={(option) => `${option.full_name} - ${option.designation}`}
                value={employees.filter((emp) => formData.team_member_ids.includes(emp.id))}
                onChange={(event, newValue) => {
                  onInputChange(
                    'team_member_ids',
                    newValue.map((emp) => emp.id)
                  );
                }}
                disabled={isViewMode}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    data-testid="team-members-autocomplete"
                    label="Team Members"
                    placeholder="Select team members"
                    helperText="Select employees to add as team members (optional)"
                    error={!!formErrors.team_member_ids}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        onSubmit(event);
                      }
                    }}
                  />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip label={option.full_name} {...getTagProps({ index })} key={option.id} />
                  ))
                }
              />
            </Grid>

            {/* Show current team info in view mode */}
            {isViewMode && selectedTeam && selectedTeam.team_members && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Team Members
                    </Typography>
                    <Stack spacing={1}>
                      {selectedTeam.team_members.length > 0 ? (
                        selectedTeam.team_members.map((member) => (
                          <Chip
                            key={member.id}
                            label={`${member.full_name} - ${member.designation}`}
                            variant="outlined"
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No team members assigned
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Show team information in view/edit mode */}
            {(isViewMode || mode === 'edit') && selectedTeam && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Team Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Created At
                        </Typography>
                        <Typography variant="body1">
                          {new Date(selectedTeam.created_at).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Status
                        </Typography>
                        <Chip
                          label={selectedTeam.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={selectedTeam.is_active ? 'success' : 'default'}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{isViewMode ? 'Close' : 'Cancel'}</Button>
        {!isViewMode && (
          <Button onClick={onSubmit} variant="contained">
            {mode === 'add' ? 'Create' : 'Update'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

TeamForm.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['add', 'edit', 'view']).isRequired,
  formData: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    team_lead_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    supervisor_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    team_member_ids: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  formErrors: PropTypes.object,
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      full_name: PropTypes.string.isRequired,
      designation: PropTypes.string,
    })
  ),
  selectedTeam: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
};

TeamForm.defaultProps = {
  formErrors: {},
  employees: [],
  selectedTeam: null,
};

export default TeamForm;
